// Updates for certain departments (Grey, 2026-10-16: "I want to redact updates per class. Meaning only certain classes can
// see an update"). The owner (or the admin) says, per update, who sees it: everyone (the default), or a chosen few classes.
// The classes are the departments, as every crew list shows them (shared/skills.ts DEPT_ORDER): command, flight, operations,
// tactical, security, engineering, science, medical, counsel, supply, then the general crew and civilians. Departments rather than the
// tandems' finer classes (helm and navigation, shared/tandem.ts) because a department is what every screen calls you, it is
// what a drafted crewman is placed in, and command, the general crew and civilians have no tandem class at all.
// An update not for your department reads REDACTED, exactly as one the owner has hidden.
import { departmentOf } from "./manifest.js";
import { DEPT_ORDER } from "./skills.js";
import type { ChangelogView } from "./protocol.js";

export const UPDATE_CLASSES: readonly string[] = DEPT_ORDER;
export const UPDATE_CLASS_LABEL: Record<string, string> = { command: "Command", flight: "Flight", operations: "Operations", tactical: "Tactical", security: "Security", engineering: "Engineering", science: "Science", medical: "Medical", counsel: "Counsel", supply: "Supply", crew: "Crew", civilian: "Civilians" };
export const UPDATE_CLASS_SHORT: Record<string, string> = { command: "CMD", flight: "FLT", operations: "OPS", tactical: "TAC", security: "SEC", engineering: "ENG", science: "SCI", medical: "MED", counsel: "CNS", supply: "SUP", crew: "CRW", civilian: "CIV" };

// the class a player reads the updates as: their billet's department; general crew are "crew" unless placed in a department's
// lower decks (a drafted crewman: that department); civilians are "civilian"; prisoners, survivors and boarders read as crew
export function updateClassOf(billet: string, dept?: string | null): string {
  if (billet === "civilian") return "civilian";
  const d = departmentOf(billet);
  if (d === "crew" && dept && dept !== "crew" && UPDATE_CLASSES.includes(dept)) return dept;
  return UPDATE_CLASSES.includes(d) ? d : "crew";
}

// a class list from a message, state.json or the code: known classes only, each once, in DEPT_ORDER. null (or every class)
// is everyone; [] is no department (only the owner reads it). undefined: not a class list at all (the message is refused)
export function cleanClasses(x: unknown): string[] | null | undefined {
  if (x === null) return null;
  if (!Array.isArray(x) || x.length > UPDATE_CLASSES.length * 2) return undefined;
  const want = new Set<string>();
  for (const v of x) { if (typeof v !== "string" || !UPDATE_CLASSES.includes(v)) return undefined; want.add(v); }
  const out = UPDATE_CLASSES.filter((c) => want.has(c));
  return out.length === UPDATE_CLASSES.length ? null : out;
}

export const seesUpdate = (classes: readonly string[] | null | undefined, cls: string) => !classes || classes.includes(cls);

// an update's own key for its class list: its date and title (several updates share a date, e.g. eleven on 2026-10-12)
export interface UpdateLike { date: string; title: string; lines: string[]; hidden?: boolean; classes?: string[] }
export const updKey = (u: UpdateLike) => `${u.date}|${u.title}`;
export const MAX_UPD_KEY = 400;
// an "updclasses" message checked (the server's half; who may send one is the server's own check): an update the ship has, by
// its key, and a clean class list (null for everyone, sent as null: a missing list is refused). null when it's not both
export function checkUpdClasses(m: { key?: unknown; classes?: unknown }, list: readonly UpdateLike[]): { key: string; classes: string[] | null } | null {
  const key = typeof m.key === "string" && m.key.length <= MAX_UPD_KEY ? m.key : "", classes = cleanClasses(m.classes);
  if (!key || classes === undefined || !list.some((u) => updKey(u) === key)) return null;
  return { key, classes };
}

// who is reading: their class; owner: unlocked with the owner key (reads everything); editor: may set the classes (the owner,
// or the admin signed in on the admin side, who reads the updates limited to other departments but not what the owner hid)
export interface UpdateViewer { cls: string; owner: boolean; editor: boolean }
export interface UpdateState { hidden: boolean; lh: boolean[]; classes: string[] | null }
// one update as this viewer may see it. Its classes go to an editor always, to anyone it is for when it is limited (the "For
// Medical" tag), and never to those it isn't for (cx: not for your department; they don't learn whom it is for)
export function updateView(u: UpdateLike, st: UpdateState, v: UpdateViewer): ChangelogView {
  const cx = !seesUpdate(st.classes, v.cls), open = (lineHid: boolean) => v.owner || (!st.hidden && !lineHid && (!cx || v.editor));
  return {
    date: u.date, title: open(false) ? u.title : null, lines: u.lines.map((l, j) => (open(!!st.lh[j]) ? l : null)), hidden: st.hidden, lh: st.lh,
    ...(v.editor ? { k: updKey(u), cls: st.classes } : !cx && st.classes ? { cls: st.classes } : {}),
    ...(cx ? { cx: true } : {}),
  };
}

// the owner's toggles: "all" puts it back to everyone; a class picked while everyone sees it limits it to that class alone;
// after that each pick adds or takes away that class (taking away the last leaves no department)
export function nextClasses(cur: readonly string[] | null, pick: string): string[] | null {
  if (pick === "all") return null;
  if (!UPDATE_CLASSES.includes(pick)) return cur ? [...cur] : null;
  if (!cur) return [pick];
  return cleanClasses(cur.includes(pick) ? cur.filter((c) => c !== pick) : [...cur, pick]) ?? null;
}
// who sees it, in words: "everyone", "no department", "Medical", "Medical, Science", "Command, Flight, Operations and 2 more"
export function classNames(classes: readonly string[] | null, max = 3): string {
  if (!classes) return "everyone";
  if (!classes.length) return "no department";
  const s = classes.map((c) => UPDATE_CLASS_LABEL[c] ?? c);
  return s.length > max ? `${s.slice(0, max).join(", ")} and ${s.length - max} more` : s.join(", ");
}
